/**
 * EmotionDashboard — Family Emotion Analytics (FlzHub Emotions tab)
 * Real API data from /api/esma/mood/daily, highlights, distribution, leaderboard
 * No mock data — all from DB.
 */
import { Award, Brain, Heart, Loader, RefreshCw, Shield, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../config/api';
import { GAZZALI_DISPLAY, virtueScoreToColor } from '../../utils/terminologyMap';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888';

interface MoodDailyEntry { date: string; hikmet: number; secaat: number; iffet: number; adalet: number }
interface HighlightAgent { agent_id: number; agent_name: string; score: number }
interface MoodHighlights { wisest: HighlightAgent | null; bravest: HighlightAgent | null; most_empathetic: HighlightAgent | null; most_just: HighlightAgent | null }
interface DistributionEntry { group: string; count: number; percentage: number }
interface MoodMapAgent { agent_id: number; agent_name: string; hikmet: number; secaat: number; iffet: number; adalet: number }

const TIER_COLORS: Record<string, string> = { NASUT: '#9CA3AF', MELEKUT: '#3B82F6', CEBERUT: '#8B5CF6', LAHUT: '#F59E0B', HAHUT: '#E5E7EB' };
const TIER_LABELS: Record<string, string> = { NASUT: 'Tier 1', MELEKUT: 'Tier 2', CEBERUT: 'Tier 3', LAHUT: 'Tier 4', HAHUT: 'Tier 5' };

export default function EmotionDashboard() {
  const [loading, setLoading] = useState(true);
  const [moodDaily, setMoodDaily] = useState<MoodDailyEntry[]>([]);
  const [highlights, setHighlights] = useState<MoodHighlights>({ wisest: null, bravest: null, most_empathetic: null, most_just: null });
  const [distribution, setDistribution] = useState<DistributionEntry[]>([]);
  const [agents, setAgents] = useState<MoodMapAgent[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dR, hR, distR, lbR] = await Promise.allSettled([
        apiFetch(`${API_URL}/api/esma/mood/daily`),
        apiFetch(`${API_URL}/api/esma/mood/highlights`),
        apiFetch(`${API_URL}/api/esma/distribution`),
        apiFetch(`${API_URL}/api/esma/leaderboard?limit=100`),
      ]);
      // Daily
      if (dR.status === 'fulfilled' && dR.value.ok) {
        const raw = await dR.value.json() as any;
        const arr = raw?.daily || raw?.data || (Array.isArray(raw) ? raw : []);
        setMoodDaily(arr.map((d: any) => ({
          date: d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
          hikmet: parseFloat(d.hikmet) || 0, secaat: parseFloat(d.secaat) || 0,
          iffet: parseFloat(d.iffet) || 0, adalet: parseFloat(d.adalet) || 0,
        })));
      }
      // Highlights
      if (hR.status === 'fulfilled' && hR.value.ok) {
        const raw = await hR.value.json() as any;
        const h = raw?.highlights || raw || {};
        const mk = (v: any) => v ? { agent_id: Number(v.id || 0), agent_name: v.agent_name || '', score: parseFloat(v.score) || 0 } : null;
        setHighlights({ wisest: mk(h.hikmet), bravest: mk(h.secaat), most_empathetic: mk(h.iffet), most_just: mk(h.adalet) });
      }
      // Distribution
      if (distR.status === 'fulfilled' && distR.value.ok) {
        const raw = await distR.value.json() as any;
        const arr = raw?.distribution || raw?.data || (Array.isArray(raw) ? raw : []);
        const total = arr.reduce((s: number, d: any) => s + (Number(d.count) || 0), 0) || 1;
        setDistribution(arr.map((d: any) => ({ group: d.group || d.mertebe_level || '?', count: Number(d.count) || 0, percentage: ((Number(d.count) || 0) / total) * 100 })));
      }
      // Leaderboard → mood map
      if (lbR.status === 'fulfilled' && lbR.value.ok) {
        const raw = await lbR.value.json() as any;
        const list = raw?.leaderboard || raw?.agents || (Array.isArray(raw) ? raw : []);
        setAgents(list.slice(0, 80).map((a: any) => {
          const gs = typeof a.gazzali_scores === 'string' ? (() => { try { return JSON.parse(a.gazzali_scores); } catch { return {}; } })() : (a.gazzali_scores || {});
          return { agent_id: Number(a.id || a.agent_id), agent_name: a.agent_name || '', hikmet: parseFloat(gs?.hikmet) || 0.5, secaat: parseFloat(gs?.secaat) || 0.5, iffet: parseFloat(gs?.iffet) || 0.5, adalet: parseFloat(gs?.adalet) || 0.5 };
        }));
      }
    } catch (e) { console.error('[EmotionDashboard]', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void fetchData(); }, [fetchData]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 8, color: 'var(--os-text-secondary, #8b949e)' }}>
      <Loader size={20} className="animate-spin" /> Loading emotions...
    </div>
  );

  const cards = [
    { key: 'wisest', label: 'Wisest', agent: highlights.wisest, icon: <Brain size={18} />, color: '#3B82F6' },
    { key: 'bravest', label: 'Bravest', agent: highlights.bravest, icon: <Shield size={18} />, color: '#F59E0B' },
    { key: 'empathetic', label: 'Most Empathetic', agent: highlights.most_empathetic, icon: <Heart size={18} />, color: '#10B981' },
    { key: 'just', label: 'Most Just', agent: highlights.most_just, icon: <Award size={18} />, color: '#8B5CF6' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Heart size={18} style={{ color: '#ec4899' }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--os-text, #e6edf3)' }}>Family Emotions</span>
        </div>
        <button onClick={() => void fetchData()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--os-text-secondary, #8b949e)', padding: 4 }}>
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Highlight Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {cards.map(c => (
          <div key={c.key} style={{
            background: 'var(--os-card, rgba(22,27,34,0.75))',
            border: '1px solid var(--os-card-border, rgba(255,255,255,0.06))',
            borderRadius: 10, padding: '14px 12px', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${c.color}15`, color: c.color }}>
              {c.icon}
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--os-text-secondary, #8b949e)', marginBottom: 2 }}>{c.label}</div>
              {c.agent ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text, #e6edf3)' }}>{c.agent.agent_name}</div>
                  <div style={{ fontSize: 11, color: c.color }}>{c.agent.score.toFixed(2)}</div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--os-text-secondary, #8b949e)' }}>—</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Two column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Mood Map */}
        <div style={{
          background: 'var(--os-card, rgba(22,27,34,0.75))',
          border: '1px solid var(--os-card-border, rgba(255,255,255,0.06))',
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text, #e6edf3)', marginBottom: 12 }}>Family Mood Map</div>
          <div style={{ position: 'relative', paddingBottom: '100%' }}>
            <svg viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              <line x1="50" y1="8" x2="50" y2="92" stroke="var(--os-border, #333)" strokeWidth="0.3" strokeDasharray="2,2" />
              <line x1="8" y1="50" x2="92" y2="50" stroke="var(--os-border, #333)" strokeWidth="0.3" strokeDasharray="2,2" />
              <text x="8" y="97" fontSize="3.5" fill="var(--os-text-secondary)">Justice</text>
              <text x="92" y="97" fontSize="3.5" fill="var(--os-text-secondary)" textAnchor="end">Wisdom</text>
              <text x="50" y="6" fontSize="3.5" fill="var(--os-text-secondary)" textAnchor="middle">Courage</text>
              <text x="50" y="99" fontSize="3.5" fill="var(--os-text-secondary)" textAnchor="middle">Balance</text>
              {agents.map(a => {
                const x = 8 + ((a.adalet + a.hikmet) / 2) * 84;
                const y = 92 - ((a.secaat + a.iffet) / 2) * 84;
                return <circle key={a.agent_id} cx={x} cy={y} r="1.5" fill={virtueScoreToColor((a.hikmet + a.secaat + a.iffet + a.adalet) / 4)} opacity={0.75}><title>{a.agent_name}</title></circle>;
              })}
            </svg>
          </div>
          <div style={{ fontSize: 11, color: 'var(--os-text-secondary, #8b949e)', textAlign: 'center', marginTop: 6 }}>{agents.length} agents</div>
        </div>

        {/* Distribution */}
        <div style={{
          background: 'var(--os-card, rgba(22,27,34,0.75))',
          border: '1px solid var(--os-card-border, rgba(255,255,255,0.06))',
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text, #e6edf3)', marginBottom: 12 }}>Tier Distribution</div>
          {distribution.length === 0 ? (
            <div style={{ color: 'var(--os-text-secondary, #8b949e)', fontSize: 12, textAlign: 'center', padding: 30 }}>No data</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {distribution.map(d => (
                <div key={d.group} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: TIER_COLORS[d.group] || '#6B7280', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--os-text-secondary, #8b949e)', minWidth: 50 }}>{TIER_LABELS[d.group] || d.group}</span>
                  <div style={{ flex: 1, height: 6, background: 'var(--os-border, #333)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${d.percentage}%`, background: TIER_COLORS[d.group] || '#6B7280', borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--os-text-secondary, #8b949e)', minWidth: 30, textAlign: 'right' }}>{d.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Daily Mood Trend */}
      <div style={{
        background: 'var(--os-card, rgba(22,27,34,0.75))',
        border: '1px solid var(--os-card-border, rgba(255,255,255,0.06))',
        borderRadius: 10, padding: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text, #e6edf3)', marginBottom: 8 }}>Daily Mood Trend</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
          {(['hikmet', 'secaat', 'iffet', 'adalet'] as const).map(v => (
            <span key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--os-text-secondary, #8b949e)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: v === 'hikmet' ? '#3B82F6' : v === 'secaat' ? '#F59E0B' : v === 'iffet' ? '#10B981' : '#8B5CF6' }} />
              {GAZZALI_DISPLAY[v]?.label || v}
            </span>
          ))}
        </div>
        {moodDaily.length === 0 ? (
          <div style={{ color: 'var(--os-text-secondary, #8b949e)', fontSize: 12, textAlign: 'center', padding: 30 }}>No mood data yet — scoring pipeline will populate this.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
            {moodDaily.map((d, i) => {
              const avg = (d.hikmet + d.secaat + d.iffet + d.adalet) / 4;
              const h = Math.max(4, avg * 80);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ width: '100%', maxWidth: 16, height: h, background: virtueScoreToColor(avg), borderRadius: 2, opacity: 0.8 }} title={`${d.date}: ${avg.toFixed(2)}`} />
                </div>
              );
            })}
          </div>
        )}
        {moodDaily.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--os-text-secondary, #8b949e)', marginTop: 4 }}>
            <span>{moodDaily[0]?.date}</span>
            <span>{moodDaily[moodDaily.length - 1]?.date}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--os-text-secondary, #8b949e)' }}>
        <TrendingUp size={12} />
        <span>{agents.length} agents · {moodDaily.length} days tracked</span>
      </div>
    </div>
  );
}
